// src/features/patient/care-treatment/api/careTimelineApi.ts

/**
 * Purpose : The Care & Treatment API:
 * the viewed profile's visits and notes, and the patient's answer to a
 * suggested visit. The response is exactly careTimelineTypes.ts.
 *
 * Contents :
 *
 * * function(getCareTimeline) : GET /api/patient/care-timeline
 * * function(getOlderCareNotes) : the next page of older notes (X-Next-Before)
 * * function(confirmNoteLink) / function(rejectNoteLink) : "Confirm visit" / "Keep unlinked"
 * * function(linkNoteToVisit) : a note with no visit, linked to the visit the patient picked
 * * function(confirmNoteLinks) : "Confirm all suggestions from this file"
 * * function(searchCareTimeline) : search the viewed profile's visits and notes
 * * function(getUploadCareSummary) : "What we found" for one upload
 */

import { apiFetch, apiFetchRaw } from "@/lib/apiClient";

/**
 * The timeline is built over every visit and note (and decrypted), so a big
 * record can take longer than the app's 30 s default: given up on at 30 s,
 * the server's answer was thrown away and the screen waited for the next one.
 */
const TIMELINE_TIMEOUT_MS = 120_000;
import type {
  CarePlanOut, CareTeamMember, CareTimelineCounts, Encounter, IdentityPending, Note, OtherDocument, SearchResponse,
  UploadSummary,
} from "@/features/patient/care-treatment/api/careTimelineTypes";

export interface CareTimelineResponse {
  encounters: Encounter[];
  notes: Note[];
  documents?: OtherDocument[];
  /** Over every visit and note (notes are paged); absent from an older server. */
  counts?: CareTimelineCounts;
  /** Uploads waiting for "Yes, this is me" (their records are left out); null when none. */
  identityPending?: IdentityPending | null;
  /** The newest upload with a PDF ("What we found"); absent from an older server. */
  uploadSummary?: UploadSummary | null;
  careTeam?: CareTeamMember[];
  carePlans?: CarePlanOut[];
  /** Not in the JSON: the X-Next-Before header, the cursor for older notes (null = none). */
  nextBefore?: string | null;
}

export interface NoteLinkChange {
  noteId: string;
  encounterId: string | null;
  linkConfidence: Note["linkConfidence"];
}

function actingHeaders(actingAsProfileId?: string): Record<string, string> {
  return actingAsProfileId ? { "X-Acting-As-Profile-Id": actingAsProfileId } : {};
}

// The last answer per profile and its ETag: a repeat GET sends If-None-Match
// and an unchanged timeline comes back as an empty 304.
const lastAnswer = new Map<string, { etag: string; data: CareTimelineResponse }>();

// function(getCareTimeline)
/**
 * Visits and notes of the profile being viewed, newest first. A conditional
 * GET: when nothing changed the server answers 304 and the last copy is reused.
 *
 * @param actingAsProfileId - The dependent being viewed, or undefined for the patient themselves.
 * @returns Encounters and notes.
 */
export async function getCareTimeline(actingAsProfileId?: string): Promise<CareTimelineResponse> {
  const key = actingAsProfileId ?? "";
  const previous = lastAnswer.get(key);
  const res = await apiFetchRaw("/api/patient/care-timeline", {
    method: "GET",
    portal: "patient",
    timeoutMs: TIMELINE_TIMEOUT_MS,
    // The browser cache stays out of it, so a 304 reaches this code as a 304.
    cache: "no-store",
    headers: { ...actingHeaders(actingAsProfileId), ...(previous ? { "If-None-Match": previous.etag } : {}) },
  });
  if (res.status === 304 && previous) return previous.data;
  const body = res.ok ? ((await res.json().catch(() => null)) as CareTimelineResponse | null) : null;
  if (!body) throw new Error("Couldn't load your visits and notes.");
  const data: CareTimelineResponse = { ...body, nextBefore: res.headers.get("X-Next-Before") };
  const etag = res.headers.get("ETag");
  if (etag) lastAnswer.set(key, { etag, data });
  else lastAnswer.delete(key);
  return data;
}

// function(getOlderCareNotes)
/**
 * The next page of older notes: everything dated before `before` (the
 * previous page's X-Next-Before). Visits always come whole with the first page.
 *
 * @param before - The cursor, a YYYY-MM-DD day.
 * @param actingAsProfileId - The dependent being viewed, or undefined.
 * @returns That page's notes and the cursor for the page after it (null = none).
 */
export async function getOlderCareNotes(
  before: string, actingAsProfileId?: string,
): Promise<{ notes: Note[]; nextBefore: string | null }> {
  const res = await apiFetchRaw(`/api/patient/care-timeline?before=${encodeURIComponent(before)}`, {
    method: "GET",
    portal: "patient",
    timeoutMs: TIMELINE_TIMEOUT_MS,
    cache: "no-store",
    headers: actingHeaders(actingAsProfileId),
  });
  const body = res.ok ? ((await res.json().catch(() => null)) as CareTimelineResponse | null) : null;
  if (!body) throw new Error("Couldn't load older notes.");
  return { notes: body.notes, nextBefore: res.headers.get("X-Next-Before") };
}

async function answer(noteId: string, action: "confirm-link" | "reject-link", actingAsProfileId?: string): Promise<NoteLinkChange> {
  const { data, error } = await apiFetch<NoteLinkChange>(
    `/api/patient/care-timeline/notes/${encodeURIComponent(noteId)}/${action}`,
    { method: "POST", portal: "patient", headers: actingHeaders(actingAsProfileId) },
  );
  if (error || !data) throw new Error(error?.message || "Couldn't save your answer.");
  return data;
}

// function(confirmNoteLink)
/** "Confirm visit": the suggested visit is the note's visit. */
export function confirmNoteLink(noteId: string, actingAsProfileId?: string): Promise<NoteLinkChange> {
  return answer(noteId, "confirm-link", actingAsProfileId);
}

// function(rejectNoteLink)
/** "Keep unlinked": the suggested visit is wrong (it won't be suggested again), or the note belongs to no visit. */
export function rejectNoteLink(noteId: string, actingAsProfileId?: string): Promise<NoteLinkChange> {
  return answer(noteId, "reject-link", actingAsProfileId);
}

// function(linkNoteToVisit)
/** A note we found no visit for: linked to the visit (or call / message) the patient picked. */
export async function linkNoteToVisit(noteId: string, encounterId: string, actingAsProfileId?: string): Promise<NoteLinkChange> {
  const { data, error } = await apiFetch<NoteLinkChange>(
    `/api/patient/care-timeline/notes/${encodeURIComponent(noteId)}/link`,
    { method: "POST", portal: "patient", headers: actingHeaders(actingAsProfileId), body: JSON.stringify({ encounterId }) },
  );
  if (error || !data) throw new Error(error?.message || "Couldn't save your answer.");
  return data;
}

export interface NoteLinkResult {
  noteId: string;
  ok: boolean;
  encounterId?: string | null;
  linkConfidence?: string;
  error?: string;
}

// function(confirmNoteLinks)
/**
 * "Confirm all suggestions from this file": each note is confirmed as on its
 * own; some may fail and are reported one by one.
 *
 * @param noteIds - The notes whose suggested visit is accepted.
 * @param actingAsProfileId - The dependent being viewed, or undefined.
 * @returns One result per note.
 */
export async function confirmNoteLinks(noteIds: string[], actingAsProfileId?: string): Promise<NoteLinkResult[]> {
  const { data, error } = await apiFetch<{ results: NoteLinkResult[] }>("/api/patient/care-timeline/notes/confirm-links", {
    method: "POST",
    portal: "patient",
    headers: { "Content-Type": "application/json", ...actingHeaders(actingAsProfileId) },
    body: JSON.stringify({ noteIds }),
  });
  if (error || !data) throw new Error(error?.message || "Couldn't save your answers.");
  return data.results ?? [];
}

// function(searchCareTimeline)
/**
 * Search the viewed profile's visits and note text. The words are sent only
 * to the server; nothing here logs them.
 *
 * @param q - What the patient typed.
 * @param actingAsProfileId - The dependent being viewed, or undefined.
 * @returns Matching visits and notes, each with a snippet and its match ranges.
 */
export async function searchCareTimeline(q: string, actingAsProfileId?: string, limit = 50): Promise<SearchResponse> {
  const params = new URLSearchParams({ q, limit: String(limit) });
  const { data, error } = await apiFetch<SearchResponse>(`/api/patient/care-timeline/search?${params.toString()}`, {
    method: "GET",
    portal: "patient",
    headers: actingHeaders(actingAsProfileId),
  });
  if (error || !data) throw new Error(error?.message || "Couldn't search your visits and notes.");
  return { visits: data.visits ?? [], notes: data.notes ?? [], total: data.total ?? 0 };
}

// function(getUploadCareSummary)
/** "What we found" for one upload (the upload result screen); null when the server has none. */
export async function getUploadCareSummary(uploadId: string, actingAsProfileId?: string): Promise<UploadSummary | null> {
  const { data, error } = await apiFetch<UploadSummary>(
    `/api/patient/care-timeline/uploads/${encodeURIComponent(uploadId)}/summary`,
    { method: "GET", portal: "patient", headers: actingHeaders(actingAsProfileId) },
  );
  if (error || !data) return null;
  return data;
}
